import { Box3, Mesh, Object3D, Vector3 } from 'three';

/**
 * Real, event-driven reactions. `idle` means "no event is overriding the resting expression
 * scheduler" — see `restingExpression` below for what actually gets shown while idle.
 */
export type PlayerMood = 'idle' | 'anticipating' | 'surprised';

export const hash = (value: string) => {
  let result = 2166136261;
  for (const char of value) result = Math.imul(result ^ char.charCodeAt(0), 16777619);
  return result >>> 0;
};

type EyeScale = [number, number];

/**
 * Everything a mood needs to pose the face: eye scale (the existing mechanism — the resting
 * palette's asymmetric/narrowed/wide eyes are all scale changes), the `happy` eye morph weight
 * (arc eyes, hopping only), and the three `Smile` mouth morph weights.
 */
type Shape = {
  eyeL: EyeScale;
  eyeR: EyeScale;
  smile: EyeScale;
  eyeMorph: number;
  mouth: { flat: number; open: number; wavy: number };
};

const ZERO_MOUTH = { flat: 0, open: 0, wavy: 0 };
const shape = (eyeL: EyeScale, eyeR: EyeScale, smile: EyeScale, eyeMorph = 0, mouth = ZERO_MOUTH): Shape =>
  ({ eyeL, eyeR, smile, eyeMorph, mouth });

/** D23: happy is reserved for hopping — arc eyes at full weight, and the mesh's own resting
 * smile held as-is (no mouth morph), for the whole hop rather than a brief landing beat. */
const HAPPY = shape([1, 1], [1, 1], [1, 1], 1);

/** Real die-event reactions. Unchanged from the pre-D23 scale-only shapes so the existing
 * `momentumBoard3d.tsx` behaviour these are driven by keeps working exactly as before. */
const ANTICIPATING = shape([1, 1.12], [1, .86], [.9, .65]);
const SURPRISED_EVENT = shape([1.08, 1.2], [1.08, 1.2], [.72, 1.35]);

/**
 * D23's approved resting palette. Six berries must never share an expression by default, so
 * these are picked and blended per-player by `restingExpression` below rather than fixed.
 * Neutral/curious are eye-scale only (the runtime already scales eyes); thinking/surprised/
 * unsure each drive exactly one `Smile` mouth morph, per docs/verification/mouth-shapes.
 */
const PALETTE: Shape[] = [
  shape([1, 1], [1, 1], [1, 1]), // neutral
  shape([1, 1.15], [1, .85], [1.04, 1]), // curious / asymmetric
  shape([1, .82], [1, .82], [1, 1], 0, { flat: 1, open: 0, wavy: 0 }), // thinking
  shape([1, 1.18], [1, 1.18], [.92, 1.1], 0, { flat: 0, open: 1, wavy: 0 }), // surprised (resting)
  shape([1, .94], [1, 1.08], [1, 1], 0, { flat: 0, open: 0, wavy: 1 }), // unsure
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerp2 = (a: EyeScale, b: EyeScale, t: number): EyeScale => [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];
const lerpShape = (a: Shape, b: Shape, t: number): Shape => ({
  eyeL: lerp2(a.eyeL, b.eyeL, t), eyeR: lerp2(a.eyeR, b.eyeR, t), smile: lerp2(a.smile, b.smile, t),
  eyeMorph: lerp(a.eyeMorph, b.eyeMorph, t),
  mouth: { flat: lerp(a.mouth.flat, b.mouth.flat, t), open: lerp(a.mouth.open, b.mouth.open, t), wavy: lerp(a.mouth.wavy, b.mouth.wavy, t) },
});

/**
 * Per-player dwell/phase window, derived from the participant id so two ids don't sit in
 * phase: `size` (a few seconds, varying per player) and `phase` (an id-specific offset into
 * that window, mirroring the `seed % 6000` trick blink timing already uses below) together
 * mean two players' mood transitions land at different instants almost all the time even when
 * their window sizes happen to coincide.
 */
export function restingWindow(id: string): { size: number; phase: number } {
  const seed = hash(id);
  const size = 3200 + (seed % 2900); // ~3.2s - 6.1s dwell per cycle
  return { size, phase: seed % size };
}

/**
 * The resting expression for participant `id` at clock `now`. Pure function of (id, now): no
 * history, no Math.random — the same id and clock always produce the same shape, and two
 * different ids land on different window sizes and phases so their transitions don't align.
 * Mathematically continuous at every window boundary (see the `t` derivation below), which is
 * what makes a mood change a blend rather than a jump.
 */
export function restingExpression(id: string, now: number): Shape {
  const seed = hash(id);
  const { size, phase } = restingWindow(id);
  const clock = now + phase;
  const windowIndex = Math.floor(clock / size);
  const offset = clock - windowIndex * size;
  const currentIndex = hash(`${seed}:${windowIndex}`) % PALETTE.length;
  const previousIndex = hash(`${seed}:${windowIndex - 1}`) % PALETTE.length;
  // A few hundred milliseconds, varying per transition (and so per mood, since the mood being
  // entered is what `currentIndex` just picked) — never an instant switch.
  const transitionMs = 250 + (hash(`${seed}:${windowIndex}:t`) % 300);
  const t = offset < transitionMs ? offset / transitionMs : 1;
  return lerpShape(PALETTE[previousIndex]!, PALETTE[currentIndex]!, t);
}

const NEUTRAL = PALETTE[0]!;

type Feature = {
  node: Object3D; center: Vector3; position: Vector3; scale: Vector3; offset: Vector3; x: number; y: number;
  morphInfluences?: number[]; morphIndex: Partial<Record<'happy' | 'flat' | 'open' | 'wavy', number>>;
};

/** The exported GLB animates Player only; these three facial children remain independent. */
export function createPlayerFace(model: Object3D, id: string) {
  const features: (Feature | undefined)[] = ['Eye_L', 'Eye_R', 'Smile'].map(name => {
    const node = model.getObjectByName(name);
    if (!node) return undefined;
    const center = new Vector3();
    const morphIndex: Feature['morphIndex'] = {};
    if (node instanceof Mesh) {
      // Basis positions only. geometry.computeBoundingBox() expands to cover every morph
      // target, so once the mouth gained flat/open/wavy shapes the box centre no longer sat
      // on the resting smile and scaling it visibly slid the mouth across the face.
      new Box3().setFromBufferAttribute(node.geometry.attributes.position!).getCenter(center);
      // Resolve morph target indices by NAME — never by hardcoded position — since a given
      // mesh's target order is an asset-authoring detail, not a contract.
      const dictionary = node.morphTargetDictionary;
      if (dictionary) {
        for (const key of ['happy', 'flat', 'open', 'wavy'] as const) {
          if (dictionary[key] !== undefined) morphIndex[key] = dictionary[key];
        }
      }
    }
    return {
      node, center, position: node.position.clone(), scale: node.scale.clone(), offset: new Vector3(), x: 1, y: 1,
      morphInfluences: node instanceof Mesh ? node.morphTargetInfluences : undefined, morphIndex,
    };
  });
  const seed = hash(id);
  let previousTime: number | undefined;
  let previousWindow = -1, blinkAt = 900;
  let eyeMorph = 0, mouthFlat = 0, mouthOpen = 0, mouthWavy = 0;

  return {
    /**
     * Call after mixer.setTime; milliseconds use the same clock as the board renderer.
     * `hopping` must be true for a travelling player's whole hop (not just its airborne arc) —
     * that's what makes happy win for the full duration D23 requires, and false the instant
     * it isn't, so the resting scheduler (which never stopped ticking underneath) resumes
     * cleanly with no pop, since it's a pure function of the same clock throughout.
     */
    update(now: number, mood: PlayerMood = 'idle', reducedMotion = false, hopping = false) {
      const dt = previousTime === undefined ? 100 : Math.max(0, Math.min(100, now - previousTime));
      previousTime = now;
      const blend = reducedMotion ? 1 : 1 - Math.exp(-dt / 65);
      // Each participant blinks at a different, irregular point in a six-second window.
      const clock = now + seed % 6000;
      const window = Math.floor(clock / 6000);
      if (window !== previousWindow) {
        blinkAt = 900 + hash(`${seed}:${window}`) % 3900;
        previousWindow = window;
      }
      const elapsed = clock % 6000 - blinkAt;
      const blink = reducedMotion || mood === 'surprised' || hopping || elapsed < 0 || elapsed > 190
        ? 1 : 1 - .94 * Math.sin(Math.PI * elapsed / 190);
      // Hopping wins outright (D23: happy for the whole hop). Otherwise a real die event wins
      // over the idle schedule. Otherwise rest on the schedule — held at neutral, uncycling,
      // under reduced motion.
      const target = hopping ? HAPPY
        : mood === 'anticipating' ? ANTICIPATING
        : mood === 'surprised' ? SURPRISED_EVENT
        : reducedMotion ? NEUTRAL
        : restingExpression(id, now);
      const eyeSmileTargets = [target.eyeL, target.eyeR, target.smile];
      features.forEach((feature, index) => {
        if (!feature) return;
        const [tx, ty] = eyeSmileTargets[index]!;
        feature.x += (tx - feature.x) * blend;
        feature.y += (ty - feature.y) * blend;
        const scale = feature.node.scale.set(
          feature.scale.x * feature.x,
          feature.scale.y * feature.y * (index < 2 ? blink : 1),
          feature.scale.z,
        );
        // Smile vertices are baked around the body origin: scale around the face, not the feet.
        const offset = feature.offset.copy(feature.scale).sub(scale).multiply(feature.center).applyQuaternion(feature.node.quaternion);
        feature.node.position.copy(feature.position).add(offset);
        if (feature.morphInfluences) {
          if (feature.morphIndex.happy !== undefined) {
            eyeMorph += (target.eyeMorph - eyeMorph) * blend;
            feature.morphInfluences[feature.morphIndex.happy] = eyeMorph;
          }
          if (feature.morphIndex.flat !== undefined) {
            mouthFlat += (target.mouth.flat - mouthFlat) * blend;
            feature.morphInfluences[feature.morphIndex.flat] = mouthFlat;
          }
          if (feature.morphIndex.open !== undefined) {
            mouthOpen += (target.mouth.open - mouthOpen) * blend;
            feature.morphInfluences[feature.morphIndex.open] = mouthOpen;
          }
          if (feature.morphIndex.wavy !== undefined) {
            mouthWavy += (target.mouth.wavy - mouthWavy) * blend;
            feature.morphInfluences[feature.morphIndex.wavy] = mouthWavy;
          }
        }
      });
    },
  };
}
